/**
 * `<PointCloudQualityPanel />` — WP-5.
 *
 * Reusable point-cloud quality slice extracted from
 * `engines/giro3d/Giro3DToolbar.tsx` so the same controls power both:
 *   - the in-canvas Giro3D toolbar overlay (existing demo / production
 *     surface, unchanged in behaviour); and
 *   - the new right-rail `<PointCloudToolCard />`.
 *
 * Spec references:
 *   - `docs/results/VIEWER-TOOLBAR-AND-GTWIN-FIRST-UX-MASTERPLAN.md` §7.5
 *     (Card 5) — point budget, point size (auto / manual), density / LOD,
 *     color mode, classification filter.
 *   - `geosoft-platform-design-system/docs/DESIGN_POLICY.md` §5 — tokens
 *     only, non-color cue per state, single primary CTA per row, 40 px touch
 *     min on every interactive control (slider thumbs implement this through
 *     a 40 px wrapping label row plus the `accentColor` style).
 *   - `geosoft-platform-design-system/docs/ICON_SYSTEM_PLAYBOOK.md` —
 *     `palette` (RGB), `terrain` (elevation), `bolt` (intensity), `category`
 *     (classification) as non-color cues on the color-mode chips.
 *
 * Controlled component: parents own the canonical snapshot and apply
 * incoming patches by calling adapters. The panel never reads or writes the
 * engine directly so it is trivially testable in isolation.
 */

import type { CSSProperties, ReactNode } from 'react';
import { Chip, Icon, tokens, type IconName } from '@/design';
import type {
  PointCloudColorMode,
  PointCloudQualitySettings,
  PointCloudQualitySnapshot,
  PointCloudShape,
} from '@/engines/giro3d/Giro3DAdapter';

// ---------------------------------------------------------------------------
// Color-mode chip plan (icon non-color cue per ICON_SYSTEM_PLAYBOOK.md)
// ---------------------------------------------------------------------------

interface ColorModeOption {
  readonly id: PointCloudColorMode;
  readonly label: string;
  readonly icon: IconName;
}

const COLOR_MODE_OPTIONS: ReadonlyArray<ColorModeOption> = [
  { id: 'auto', label: 'Auto', icon: 'auto_awesome' },
  { id: 'rgb', label: 'RGB', icon: 'palette' },
  { id: 'elevation', label: 'Elevation', icon: 'terrain' },
  { id: 'intensity', label: 'Intensity', icon: 'bolt' },
  { id: 'classification', label: 'Class', icon: 'category' },
];

/**
 * Cycle order documented in the masterplan §7.5 acceptance test ("RGB →
 * elevation → intensity → classification → RGB"). Auto is excluded from the
 * keyboard cycle since it is a "pick the best supported attribute" mode
 * rather than a user-visible color.
 */
export const COLOR_MODE_CYCLE: ReadonlyArray<PointCloudColorMode> = [
  'rgb',
  'elevation',
  'intensity',
  'classification',
];

/** Pure helper used by `<PointCloudToolCard />` and tests. */
export function nextColorMode(
  current: PointCloudColorMode,
): PointCloudColorMode {
  const idx = COLOR_MODE_CYCLE.indexOf(current);
  if (idx === -1) return COLOR_MODE_CYCLE[0];
  return COLOR_MODE_CYCLE[(idx + 1) % COLOR_MODE_CYCLE.length];
}

// ---------------------------------------------------------------------------
// Density / LOD chip plan
// ---------------------------------------------------------------------------

interface DensityOption {
  readonly id: 'coarse' | 'balanced' | 'fine';
  readonly label: string;
  readonly icon: IconName;
  readonly subdivisionThreshold: number;
  readonly description: string;
}

export const DENSITY_OPTIONS: ReadonlyArray<DensityOption> = [
  {
    id: 'coarse',
    label: 'Coarse',
    icon: 'speed',
    subdivisionThreshold: 1.5,
    description: 'Coarser LOD, higher frame-rate for navigation.',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    icon: 'tune',
    subdivisionThreshold: 0.75,
    description: 'Recommended default — balanced density and performance.',
  },
  {
    id: 'fine',
    label: 'Fine',
    icon: 'auto_awesome',
    subdivisionThreshold: 0.35,
    description: 'Finer LOD, higher density, slower navigation.',
  },
];

/** Pure helper: closest density preset for a given subdivision threshold. */
export function densityPresetFor(value: number): DensityOption['id'] {
  let best = DENSITY_OPTIONS[0];
  let bestDelta = Math.abs(best.subdivisionThreshold - value);
  for (const option of DENSITY_OPTIONS) {
    const delta = Math.abs(option.subdivisionThreshold - value);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = option;
    }
  }
  return best.id;
}

// ---------------------------------------------------------------------------
// Point shape chip plan (potree-core PointShape enum)
// ---------------------------------------------------------------------------

interface ShapeOption {
  readonly id: PointCloudShape;
  readonly label: string;
  readonly icon: IconName;
  readonly description: string;
}

/**
 * Native Potree exposes square (fastest), circle (cleanest inspection look),
 * and paraboloid (faux-spherical "splat" highlight). We map the same three
 * onto the in-canvas tool card.
 */
export const SHAPE_OPTIONS: ReadonlyArray<ShapeOption> = [
  {
    id: 'square',
    label: 'Square',
    icon: 'crop_square',
    description: 'Fastest — flat square sprites, native Potree default.',
  },
  {
    id: 'circle',
    label: 'Circle',
    icon: 'radio_button_unchecked',
    description: 'Round splats — best for close inspection.',
  },
  {
    id: 'paraboloid',
    label: 'Splat',
    icon: 'blur_on',
    description: 'Paraboloid highlight — splat-like surface look.',
  },
];

// ---------------------------------------------------------------------------
// Bounds (kept in sync with Giro3DAdapter defaults)
// ---------------------------------------------------------------------------

/**
 * Sub-pixel sizes are useful for dense (>50M point) clouds where you want
 * to see the underlying geometry instead of a soup of overlapping splats.
 */
export const POINT_SIZE_MIN = 0.25;
export const POINT_SIZE_MAX = 8;
export const POINT_SIZE_STEP = 0.25;

export const POINT_BUDGET_MIN_MILLIONS = 2;
export const POINT_BUDGET_MAX_MILLIONS = 80;
export const POINT_BUDGET_STEP_MILLIONS = 2;

export const INTENSITY_GAMMA_MIN = 0.4;
export const INTENSITY_GAMMA_MAX = 1.6;
export const INTENSITY_GAMMA_STEP = 0.05;
export const INTENSITY_CONTRAST_MIN = -0.6;
export const INTENSITY_CONTRAST_MAX = 0.8;
export const INTENSITY_CONTRAST_STEP = 0.05;
export const INTENSITY_BRIGHTNESS_MIN = -0.4;
export const INTENSITY_BRIGHTNESS_MAX = 0.5;
export const INTENSITY_BRIGHTNESS_STEP = 0.02;

interface IntensityPreset {
  readonly id: 'raw' | 'balanced' | 'inspection';
  readonly label: string;
  readonly icon: IconName;
  readonly values: Pick<
    PointCloudQualitySettings,
    'intensityGamma' | 'intensityContrast' | 'intensityBrightness'
  >;
  readonly description: string;
}

interface IntensityBackgroundOption {
  readonly id: PointCloudQualitySettings['intensityBackground'];
  readonly label: string;
  readonly icon: IconName;
  readonly description: string;
}

export const INTENSITY_BACKGROUND_OPTIONS: ReadonlyArray<IntensityBackgroundOption> = [
  {
    id: 'dark',
    label: 'Dark bg',
    icon: 'bolt',
    description: 'Dark background for color-rich RGB and low-glare viewing.',
  },
  {
    id: 'light',
    label: 'White bg',
    icon: 'visibility',
    description: 'White background for clearer grayscale intensity inspection.',
  },
];

export const INTENSITY_PRESETS: ReadonlyArray<IntensityPreset> = [
  {
    id: 'raw',
    label: 'Raw',
    icon: 'tune',
    values: { intensityGamma: 1, intensityContrast: 0, intensityBrightness: 0 },
    description: 'Original intensity mapping with no extra brightening.',
  },
  {
    id: 'balanced',
    label: 'Balanced',
    icon: 'tune',
    values: { intensityGamma: 0.9, intensityContrast: 0.15, intensityBrightness: 0.12 },
    description: 'Brighter and clearer while keeping natural depth cues.',
  },
  {
    id: 'inspection',
    label: 'Inspection',
    icon: 'visibility',
    values: { intensityGamma: 0.75, intensityContrast: 0.28, intensityBrightness: 0.2 },
    description: 'Maximum local detail for dark industrial scans.',
  },
];

export function intensityPresetFor(
  quality: Pick<
    PointCloudQualitySettings,
    'intensityGamma' | 'intensityContrast' | 'intensityBrightness'
  >,
): IntensityPreset['id'] {
  let best = INTENSITY_PRESETS[0];
  let bestDelta = Number.POSITIVE_INFINITY;
  for (const preset of INTENSITY_PRESETS) {
    const delta =
      Math.abs(quality.intensityGamma - preset.values.intensityGamma) +
      Math.abs(quality.intensityContrast - preset.values.intensityContrast) +
      Math.abs(quality.intensityBrightness - preset.values.intensityBrightness);
    if (delta < bestDelta) {
      bestDelta = delta;
      best = preset;
    }
  }
  return best.id;
}

// ---------------------------------------------------------------------------
// Classification filter (ASPRS LAS standard subset for the chip group)
// ---------------------------------------------------------------------------

export interface ClassificationOption {
  readonly classCode: number;
  readonly label: string;
  readonly icon: IconName;
}

/**
 * The icon registry does not yet include a vegetation-specific glyph. We
 * reuse `landscape` (approved playbook glyph for "Contour / landscape") for
 * the vegetation classification chip; the visible "Vegetation" label is the
 * primary non-color cue. Tracked in design-risks of the WP-5 brief.
 */
export const ASPRS_CLASSIFICATIONS: ReadonlyArray<ClassificationOption> = [
  { classCode: 2, label: 'Ground', icon: 'terrain' },
  { classCode: 5, label: 'Vegetation', icon: 'landscape' },
  { classCode: 6, label: 'Building', icon: 'architecture' },
  { classCode: 9, label: 'Water', icon: 'waves' },
  { classCode: 7, label: 'Noise', icon: 'warning' },
];

function resolveClassificationIcon(option: ClassificationOption): IconName {
  return option.icon;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface PointCloudQualityPanelProps {
  /** Live snapshot from the adapter (or a controlled mock in tests). */
  quality: PointCloudQualitySnapshot;
  /** Patch handler — wired by the parent to
   *  `Giro3DAdapter.setPointCloudQuality` / `PotreeAdapter.setPointCloudQuality`. */
  onChange(next: Partial<PointCloudQualitySettings>): void;
  /** Active classification class codes (empty set = "show all"). */
  classificationFilter?: ReadonlySet<number>;
  /** Toggle handler for the classification chip group. */
  onToggleClassification?(classCode: number): void;
  /** Show all / clear filter. */
  onClearClassificationFilter?(): void;
  /** Optional footer slot — caller may render a Banner or status line. */
  footer?: ReactNode;
  /** When true, render the panel inside its host (no inline `PanelCard`). */
  embedded?: boolean;
  'data-testid'?: string;
}

export function PointCloudQualityPanel({
  quality,
  onChange,
  classificationFilter,
  onToggleClassification,
  onClearClassificationFilter,
  footer,
  embedded = false,
  'data-testid': testId,
}: PointCloudQualityPanelProps) {
  const budgetMillions = quality.pointBudget
    ? Math.round(quality.pointBudget / 1_000_000)
    : POINT_BUDGET_MIN_MILLIONS;
  const pointSizeLabel = quality.pointSize === 0
    ? 'Auto'
    : `${quality.pointSize.toFixed(1)} px`;
  const totalPoints = quality.pointCount ?? quality.displayedPointCount ?? 0;
  const activeDensity = densityPresetFor(quality.subdivisionThreshold);
  const activeIntensityPreset = intensityPresetFor(quality);
  const activeFilter = classificationFilter ?? new Set<number>();
  const classificationChipsEnabled =
    quality.colorMode === 'classification' &&
    quality.supportedColorModes.includes('classification');

  const containerStyle: CSSProperties = embedded
    ? { display: 'flex', flexDirection: 'column', gap: tokens.space[3] }
    : {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[3],
        background: tokens.color.surface[1],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
        padding: tokens.space[3],
        maxWidth: tokens.width.popoverMd,
      };

  return (
    <section
      aria-label="Point cloud quality controls"
      data-testid={testId ?? 'point-cloud-quality-panel'}
      style={containerStyle}
    >
      <SectionLabel
        icon="tune"
        label="Quality"
        helper="Adjust point budget, sizing, and LOD."
      />

      <QualitySlider
        id="point-budget"
        label="Point budget"
        ariaLabel="Point budget in millions of points"
        value={budgetMillions}
        min={POINT_BUDGET_MIN_MILLIONS}
        max={POINT_BUDGET_MAX_MILLIONS}
        step={POINT_BUDGET_STEP_MILLIONS}
        suffix={`${budgetMillions}M pts`}
        onChange={(value) =>
          onChange({ pointBudget: value * 1_000_000 })
        }
      />

      <QualitySlider
        id="point-size"
        label="Point size"
        ariaLabel="Point size in pixels (0 = auto)"
        value={quality.pointSize}
        min={POINT_SIZE_MIN}
        max={POINT_SIZE_MAX}
        step={POINT_SIZE_STEP}
        suffix={pointSizeLabel}
        onChange={(value) => onChange({ pointSize: value })}
      />

      <ChipRow
        ariaLabel="Density and level of detail preset"
        chips={DENSITY_OPTIONS.map((option) => ({
          key: option.id,
          icon: option.icon,
          label: option.label,
          pressed: option.id === activeDensity,
          onClick: () =>
            onChange({ subdivisionThreshold: option.subdivisionThreshold }),
          title: option.description,
        }))}
      />

      <SectionLabel
        icon="blur_on"
        label="Point shape"
        helper="Square (fastest), Circle (cleanest), Splat (paraboloid)."
      />
      <ChipRow
        ariaLabel="Point shape"
        chips={SHAPE_OPTIONS.map((option) => ({
          key: option.id,
          icon: option.icon,
          label: option.label,
          pressed: quality.shape === option.id,
          onClick: () => onChange({ shape: option.id }),
          title: option.description,
        }))}
      />

      <CheckboxRow
        checked={quality.occlusion}
        onChange={(value) => onChange({ occlusion: value })}
        label="Eye-dome occlusion (depth contrast)"
      />

      <SectionLabel
        icon="palette"
        label="Color mode"
        helper="Cycle with the keyboard via [/] in the tool card."
      />
      <ChipRow
        ariaLabel="Point cloud color mode"
        chips={COLOR_MODE_OPTIONS.map((option) => {
          return {
            key: option.id,
            icon: option.icon,
            label: option.label,
            pressed: quality.colorMode === option.id,
            onClick: () => onChange({ colorMode: option.id }),
            title: `Color points by ${option.label.toLowerCase()}`,
          };
        })}
      />

      <SectionLabel
        icon="bolt"
        label="Intensity tuning"
        helper="Lift dark scans with brightness, contrast, and gamma."
      />
      <ChipRow
        ariaLabel="Intensity preset"
        chips={INTENSITY_PRESETS.map((preset) => ({
          key: preset.id,
          icon: preset.icon,
          label: preset.label,
          pressed: activeIntensityPreset === preset.id,
          onClick: () => onChange({ ...preset.values }),
          title: preset.description,
        }))}
      />
      <ChipRow
        ariaLabel="Intensity background"
        chips={INTENSITY_BACKGROUND_OPTIONS.map((option) => ({
          key: option.id,
          icon: option.icon,
          label: option.label,
          pressed: quality.intensityBackground === option.id,
          onClick: () => onChange({ intensityBackground: option.id }),
          title: option.description,
        }))}
      />
      <QualitySlider
        id="intensity-brightness"
        label="Brightness"
        ariaLabel="Intensity brightness adjustment"
        value={quality.intensityBrightness}
        min={INTENSITY_BRIGHTNESS_MIN}
        max={INTENSITY_BRIGHTNESS_MAX}
        step={INTENSITY_BRIGHTNESS_STEP}
        suffix={quality.intensityBrightness.toFixed(2)}
        onChange={(value) => onChange({ intensityBrightness: value })}
      />
      <QualitySlider
        id="intensity-contrast"
        label="Contrast"
        ariaLabel="Intensity contrast adjustment"
        value={quality.intensityContrast}
        min={INTENSITY_CONTRAST_MIN}
        max={INTENSITY_CONTRAST_MAX}
        step={INTENSITY_CONTRAST_STEP}
        suffix={quality.intensityContrast.toFixed(2)}
        onChange={(value) => onChange({ intensityContrast: value })}
      />
      <QualitySlider
        id="intensity-gamma"
        label="Gamma"
        ariaLabel="Intensity gamma adjustment"
        value={quality.intensityGamma}
        min={INTENSITY_GAMMA_MIN}
        max={INTENSITY_GAMMA_MAX}
        step={INTENSITY_GAMMA_STEP}
        suffix={quality.intensityGamma.toFixed(2)}
        onChange={(value) => onChange({ intensityGamma: value })}
      />

      <SectionLabel
        icon="category"
        label="Classification filter"
        helper={
          classificationChipsEnabled
            ? 'Show selected classes only — empty = show all.'
            : 'Switch color mode to Class to filter by classification.'
        }
      />
      <ChipRow
        ariaLabel="ASPRS classification filter"
        chips={ASPRS_CLASSIFICATIONS.map((option) => ({
          key: String(option.classCode),
          icon: resolveClassificationIcon(option),
          label: `${option.label}`,
          pressed: activeFilter.has(option.classCode),
          disabled: !classificationChipsEnabled,
          onClick: () => onToggleClassification?.(option.classCode),
          title: `Toggle classification class ${option.classCode} — ${option.label}`,
        }))}
        trailing={
          classificationChipsEnabled && activeFilter.size > 0 ? (
            <Chip
              icon="visibility"
              label="Show all"
              pressable
              onClick={() => onClearClassificationFilter?.()}
              aria-label="Clear classification filter — show all classes"
            />
          ) : null
        }
      />

      <ReadoutRow
        items={[
          {
            key: 'layers',
            icon: 'layers',
            label: `${quality.layerCount} layer${
              quality.layerCount === 1 ? '' : 's'
            }`,
            ariaLabel: `${quality.layerCount} active point cloud layer${
              quality.layerCount === 1 ? '' : 's'
            }`,
          },
          {
            key: 'points',
            icon: 'blur_on',
            label: totalPoints
              ? `${totalPoints.toLocaleString()} pts`
              : '— pts',
            ariaLabel: totalPoints
              ? `${totalPoints.toLocaleString()} points loaded`
              : 'Point count unavailable',
            mono: true,
          },
          {
            key: 'size',
            icon: 'tune',
            label: pointSizeLabel,
            ariaLabel: `Current point size: ${pointSizeLabel}`,
            mono: true,
          },
        ]}
      />

      {footer ? <div>{footer}</div> : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Subcomponents (token-only, no raw constants)
// ---------------------------------------------------------------------------

interface ChipDefn {
  readonly key: string;
  readonly icon: IconName;
  readonly label: string;
  readonly pressed?: boolean;
  readonly disabled?: boolean;
  readonly onClick: () => void;
  readonly title?: string;
}

interface ChipRowProps {
  readonly chips: ReadonlyArray<ChipDefn>;
  readonly ariaLabel: string;
  readonly trailing?: ReactNode;
}

function ChipRow({ chips, ariaLabel, trailing }: ChipRowProps) {
  return (
    <div
      role="group"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.space[1],
        alignItems: 'center',
      }}
    >
      {chips.map((chip) => (
        <span
          key={chip.key}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: tokens.density.touchMin,
          }}
          title={chip.title}
        >
          <Chip
            icon={chip.icon}
            label={chip.label}
            variant={chip.pressed ? 'info' : 'neutral'}
            pressable
            pressed={chip.pressed}
            onClick={chip.disabled ? undefined : chip.onClick}
            aria-label={`${chip.label}${chip.pressed ? ' (active)' : ''}${
              chip.disabled ? ' (unavailable)' : ''
            }`}
          />
        </span>
      ))}
      {trailing}
    </div>
  );
}

interface QualitySliderProps {
  readonly id: string;
  readonly label: string;
  readonly ariaLabel: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly suffix: string;
  readonly onChange: (value: number) => void;
}

function QualitySlider({
  id,
  label,
  ariaLabel,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: QualitySliderProps) {
  const inputId = `pcq-${id}`;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `${tokens.layout.profileToolbarHeight * 2.1}px 1fr auto`,
        alignItems: 'center',
        gap: tokens.space[2],
        minHeight: tokens.density.touchMin,
      }}
    >
      <label
        htmlFor={inputId}
        style={{
          color: tokens.color.ink.secondary,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.label.size,
          fontWeight: tokens.font.role.label.weight,
        }}
      >
        {label}
      </label>
      <input
        id={inputId}
        type="range"
        role="slider"
        aria-label={ariaLabel}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-valuetext={suffix}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.currentTarget.value))}
        style={{
          width: '100%',
          accentColor: tokens.color.accent.primary,
          minHeight: tokens.density.touchMin,
        }}
      />
      <output
        htmlFor={inputId}
        aria-live="polite"
        style={{
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.mono,
          fontSize: tokens.font.role.engMono.size,
          minWidth: tokens.layout.profileToolbarHeight,
          textAlign: 'right',
        }}
      >
        {suffix}
      </output>
    </div>
  );
}

function CheckboxRow({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.space[2],
        color: tokens.color.ink.secondary,
        cursor: 'pointer',
        minHeight: tokens.density.touchMin,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        style={{
          width: tokens.space[4],
          height: tokens.space[4],
          accentColor: tokens.color.accent.primary,
        }}
      />
      <span
        style={{
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.body.size,
        }}
      >
        {label}
      </span>
    </label>
  );
}

function SectionLabel({
  icon,
  label,
  helper,
}: {
  icon: IconName;
  label: string;
  helper?: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.space[1],
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: tokens.space[1],
          color: tokens.color.ink.primary,
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.role.label.size,
          fontWeight: tokens.font.role.label.weight,
          textTransform: 'uppercase',
          letterSpacing: tokens.border.width.thin,
        }}
      >
        <Icon name={icon} size={16} decorative />
        {label}
      </span>
      {helper ? (
        <span
          style={{
            color: tokens.color.ink.muted,
            fontFamily: tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
          }}
        >
          {helper}
        </span>
      ) : null}
    </div>
  );
}

interface ReadoutItem {
  readonly key: string;
  readonly icon: IconName;
  readonly label: string;
  readonly ariaLabel: string;
  readonly mono?: boolean;
}

function ReadoutRow({ items }: { items: ReadonlyArray<ReadoutItem> }) {
  return (
    <div
      role="group"
      aria-label="Point cloud quality readout"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.space[2],
        alignItems: 'center',
      }}
    >
      {items.map((item) => (
        <span
          key={item.key}
          aria-label={item.ariaLabel}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[1],
            color: tokens.color.ink.secondary,
            fontFamily: item.mono
              ? tokens.font.family.mono
              : tokens.font.family.ui,
            fontSize: tokens.font.role.engMeta.size,
            border: tokens.border.thin,
            borderRadius: tokens.radius.chip,
            padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
            minHeight: tokens.density.rowDense,
          }}
        >
          <Icon name={item.icon} size={16} decorative />
          <span>{item.label}</span>
        </span>
      ))}
    </div>
  );
}
